import { COMMA, ENTER } from '@angular/cdk/keycodes';
import { NgComponentOutlet } from '@angular/common';
import { Component, EventEmitter, inject, Input, OnChanges, Output, type SimpleChanges, type Type } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatChipInputEvent, MatChipsModule } from '@angular/material/chips';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { MatSlideToggleChange, MatSlideToggleModule } from '@angular/material/slide-toggle';
import { TranslateModule, TranslateService } from '@ngx-translate/core';

import { EntityManagerService } from 'src/app/core';
import { ImagesManagerService, ItemThumbnailType } from 'src/app/core/services/images-manager';
import { AlertDialogService, ItemListSelectorDialogService } from 'src/app/dialogs';
import { ActionBtnComponent } from 'src/app/shared/components/master-detail-layout/action-btn/action-btn.component';

import type {
    ActionSettingDefinition,
    DeviceSettingDefinition,
    PreferenceSettingDefinition,
    SettingDefinition,
    SettingError,
    SettingListValue,
    SettingOption,
    SettingRecord,
    SettingsActionId,
    SettingsContext,
    SettingsLookupId,
    SettingValue
} from '../../models/settings-hub.model';

import { addListItem, isEmptyRecord, isPreferenceSetting } from '../../utils/settings-hub.utils';
import { SETTINGS_COMPONENTS } from '../settings-components';
import { SettingsObjectListComponent } from '../settings-object-list/settings-object-list.component';

const MAX_IMAGE_WIDTH = 1280;
const MAX_IMAGE_HEIGHT = 720;

/**
 * Renders any setting according to its `control`. It only shows the value it receives and emits the new one:
 * validation, rules and saving belong to the state service.
 */
@Component({
    selector: 'app-settings-field',
    standalone: true,
    imports: [
        ActionBtnComponent,
        FormsModule,
        MatButtonModule,
        MatChipsModule,
        MatFormFieldModule,
        MatIconModule,
        MatInputModule,
        MatSelectModule,
        MatSlideToggleModule,
        NgComponentOutlet,
        SettingsObjectListComponent,
        TranslateModule
    ],
    templateUrl: './settings-field.component.html',
    host: { class: 'tw-block' }
})
export class SettingsFieldComponent implements OnChanges {
    private readonly alertDialogService = inject(AlertDialogService);
    private readonly entityManagerService = inject(EntityManagerService);
    private readonly imagesManagerService = inject(ImagesManagerService);
    private readonly itemListSelectorDialogService = inject(ItemListSelectorDialogService);
    private readonly translateService = inject(TranslateService);

    @Input({ required: true }) setting!: SettingDefinition;
    @Input({ required: true }) context!: SettingsContext;
    @Input() value: SettingValue = null;
    @Input() disabled = false;
    @Input() error: SettingError | null = null;
    @Input() lookup?: (lookupId: SettingsLookupId) => Promise<number | undefined>;
    @Output() valueChange = new EventEmitter<SettingValue>();
    @Output() actionRun = new EventEmitter<SettingsActionId>();

    protected preference?: PreferenceSettingDefinition;
    protected action?: ActionSettingDefinition;
    protected device?: DeviceSettingDefinition;
    protected componentType?: Type<unknown>;
    protected options: SettingOption[] = [];
    protected itemName = '';
    protected readonly chipSeparatorKeys = [ENTER, COMMA];

    ngOnChanges(changes: SimpleChanges) {
        if (changes['setting']) {
            this.preference = isPreferenceSetting(this.setting) ? this.setting : undefined;
            this.action = this.setting.control === 'action' ? this.setting : undefined;
            this.device = this.setting.control === 'device-toggle' ? this.setting : undefined;
            this.componentType = this.setting.control === 'component' ? SETTINGS_COMPONENTS[this.setting.componentId] : undefined;
        }

        if (this.haveOptionsChanged(changes)) {
            this.options = this.preference?.options?.(this.context) ?? [];
        }

        if (this.preference?.control === 'item-picker' && (changes['setting'] || changes['value'])) {
            this.loadItemName();
        }
    }

    /** The options are computed again only when the setting, the loaded collections or the preferences they depend on change, not at every edit */
    private haveOptionsChanged(changes: SimpleChanges) {
        if (changes['setting']) {
            return true;
        }

        const previousContext: SettingsContext | undefined = changes['context']?.previousValue;

        if (!previousContext) {
            return false;
        }

        return previousContext.data !== this.context.data
            || !!this.preference?.optionsDependOn?.some(key => previousContext.values[key] !== this.context.values[key]);
    }

    protected async pickNumber(lookupId: SettingsLookupId) {
        const value = await this.lookup?.(lookupId);

        if (value !== undefined) {
            this.valueChange.emit(value);
        }
    }

    protected get listValue(): SettingListValue {
        return Array.isArray(this.value) ? this.value as SettingListValue : [];
    }

    /** The numbers of the texts of a numbered series: as many as the preference of the setting says */
    protected get textIndexes(): number[] {
        const countKey = this.preference?.indexedTexts?.countKey;
        const count = countKey ? Number(this.context.values[countKey]) || 0 : 0;

        return Array.from({ length: count }, (_, index) => index + 1);
    }

    protected textAt(index: number) {
        return String(this.listValue[index - 1] ?? '');
    }

    protected setIndexedText(index: number, text: string) {
        const texts = this.textIndexes.map(textIndex => this.textAt(textIndex));
        texts[index - 1] = text;

        this.valueChange.emit(texts);
    }

    protected async onImageSelected(event: Event) {
        const input = event.target as HTMLInputElement;
        const file = input.files?.[0];
        input.value = '';

        if (!file) {
            return;
        }

        try {
            const image = await ImagesManagerService.localLoadNewImage(file, MAX_IMAGE_WIDTH, MAX_IMAGE_HEIGHT);

            if (typeof image === 'object') {
                this.valueChange.emit(await this.imagesManagerService.uploadImage(image as ItemThumbnailType));
            }
        } catch (messageLabel: any) {
            this.alertDialogService.openDialog({ data: { messageLabel: typeof messageLabel === 'string' ? messageLabel : 'APPLICATION.PROBLEM_OPENING_IMAGE' } });
        }
    }

    protected get recordsValue(): SettingRecord[] {
        return Array.isArray(this.value) ? this.value as SettingRecord[] : [];
    }

    protected get canAddChip() {
        return this.preference?.maxItems === undefined || this.listValue.length < this.preference.maxItems;
    }

    protected addChip(event: MatChipInputEvent) {
        const list = addListItem(this.listValue, event.value, { maxItems: this.preference?.maxItems, filter: this.preference?.chipFilter });

        if (list !== this.listValue) {
            this.valueChange.emit(list);
        }

        event.chipInput.clear();
    }

    protected removeChip(item: string | number) {
        this.valueChange.emit(this.listValue.filter(listItem => listItem !== item));
    }

    /** The errors of the rows of a list are shown under their fields; the one of the whole list only when no row has been started */
    protected get hasStartedRows() {
        return this.recordsValue.some(item => !isEmptyRecord(item));
    }

    protected get errorLabel() {
        return this.error === 'required' ? 'NG_MESSAGES.REQUIRED_FIELD' : 'NG_MESSAGES.INVALID_VALUE';
    }

    /**
     * The switch shows the state of the device, which can refuse the change (e.g. the user closes the dialog that is asked first):
     * so the switch goes back to the state of the device, and it moves when the device does and the new value arrives.
     */
    protected onDeviceToggle(event: MatSlideToggleChange) {
        this.valueChange.emit(event.checked);
        event.source.checked = this.value === true;
    }

    protected async pickItem() {
        try {
            const items = await this.entityManagerService.items.fetchCollectionOffline();

            const result = await this.itemListSelectorDialogService.openDialog({
                data: {
                    message: this.translateService.instant(this.setting.label),
                    items: items
                        .map(item => ({ id: item.id, name: item.name }))
                        .sort((itemA, itemB) => itemA.name.localeCompare(itemB.name)),
                    options: { disableSetAsDefault: true }
                }
            });

            if (result?.element) {
                this.valueChange.emit(result.element.id);
            }
        } catch {
            this.alertDialogService.openDialog({ data: { messageLabel: this.translateService.instant('SETTINGS.SETTINGS.SAVE_EDIT_ERROR') } });
        }
    }

    private async loadItemName() {
        const itemId = this.value;

        // 0 is the default of a cover that has no product yet
        if (typeof itemId !== 'number' || !itemId) {
            this.itemName = '';
            return;
        }

        // A product that cannot be read is shown as empty: the value is still there
        const item = await this.entityManagerService.items.fetchOneOffline(itemId).catch(() => undefined);

        // Ignore the answer if the value changed in the meantime
        if (itemId === this.value) {
            this.itemName = item?.name ?? '';
        }
    }
}
